import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { Manifest } from '../api/authoring'
import { useTargetDocument } from '../api/useTargetDocument'
import { nameOf } from '../model/name'

/**
 * What a link points at, in words rather than in its id.
 *
 * **A component, not a hook, because it draws one row at a time.** The
 * name lives in the target's own stream, so saying it means reading that
 * document — and a control drawing a list of placements cannot call a
 * hook per row. One of these per row can.
 *
 * The id is not dropped, it moves: build mode is the register where the
 * model is visible on purpose, and "which component is this" is a
 * question an operator does ask — just not on every row at once. It is
 * on the hover, next to the revision the placement resolves to.
 *
 * **A target with no name of its own still shows its id**, and so does a
 * row whose read is still in flight. That is what every row showed
 * before any of this and it is never wrong — where the alternative was
 * repeating what the row already says two words to the left, because a
 * type nobody labelled and that holds no words is drawn beside its own
 * type's name.
 */
export function ComponentName({
  tenant,
  id,
  revision,
  manifest,
  prefix,
}: {
  tenant: string
  /** The target's own id, bare. */
  id: string
  /** The revision a pinned edge resolves to. Absent follows the latest. */
  revision?: number
  manifest: Manifest | undefined
  /** Drawn before the name, where a row marks the link with an arrow. */
  prefix?: string
}) {
  const target = useTargetDocument(tenant, id, revision)
  const name = nameOf(target.data, manifest)

  // The id describes the name and does not replace it. A string tooltip
  // otherwise becomes its child's `aria-label`, and a screen reader read
  // a uuid wherever a sighted author read "Home".
  return (
    <Tooltip describeChild title={revision === undefined ? id : `${id} · r${revision}`}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
        {prefix}
        {target.isLoading || !name ? id : name}
      </Typography>
    </Tooltip>
  )
}
